import { Component, h, Element, Prop, State, Listen, Fragment } from '@stencil/core';
import { Dayjs } from 'dayjs';
import { i18n } from '../../utils/I18n';
import { getPrivacyPolicyLink, validateEmail } from '../../helpers/utils';
import { focusTrap } from '@oebb-web-team/oebb-web-components';
import { ServiceContainer } from '../../services/service-container';
import { NotifierService } from '../../services/notifier.service';
import { Language, SubscriptionRequest } from '../../http/nj-notifier';
import { formatIsoDate } from '../../utils/date';
import { NjLoaderSize } from '../nj-loader/nj-loader-size';
import { JSX } from '@stencil/core/internal';

@Component({
  tag: 'nj-notifier',
  styleUrl: 'nj-notifier.scss',
  shadow: false,
})

export class NjNotifier {
    @Element() el: HTMLElement;

    @Prop() departureDate: Dayjs
    @Prop() departureStation: string
    @Prop() departureNumber: string
    @Prop() arrivalDate: Dayjs
    @Prop() arrivalStation: string
    @Prop() arrivalNumber: string
    @Prop() train: string;

    @State() isOpen = false;
    @State() isLoading = false;
    @State() showValidation: boolean = false;
    @State() privacyPolicyAccepted: boolean = false;
    @State() isEmailValid: boolean = null;
    @State() hasValidForm: boolean = false;

    @Listen('keydown', {target: 'window'})
    async onKeydown(event: KeyboardEvent) {
        if (!this.isOpen) {
			// Ignore everything if overlay is closed
			return;
		}

        switch (event.key) {
			case 'Tab':
				focusTrap(this.el, event);
				break;
            case 'Escape':
                this.closeNotifierDialog();
                break;
        }
    }

    @Listen('click', {target: 'window'})
    async onClick(event: MouseEvent) {
        if (!this.isOpen) return;

        if (!event.composedPath().includes(this.el)) {
            this.closeNotifierDialog();
        }
    }

    private handleTogglerClick(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        this.isOpen ? this.closeNotifierDialog() : this.openNotifierDialog();
    }

    private handleEmailInput(e: Event) {
        let element: HTMLInputElement = e.target as HTMLInputElement;
        this.isEmailValid = validateEmail(element.value) ? true : false;
    }

    private handlePrivacyPolicyCheckbox(e: Event) {
        e.preventDefault();

		this.privacyPolicyAccepted = (e.target as HTMLInputElement).checked;
	}

    private async handleSubmitClick(e: Event) {
        e.preventDefault();
        this.validateForm();

        if (this.hasValidForm) {
            this.isLoading = true;

            const postData: SubscriptionRequest = {
                'email': this.el.querySelector('nj-input-field')?.value,
                'language': Language[i18n.getLang()?.toUpperCase()] || Language.EN,
                'trainDate': formatIsoDate(this.departureDate),
                'trainNumber': this.train,
                'from': this.departureNumber,
                'to': this.arrivalNumber
            }

            try {
                await NotifierService.instance.subscribe(postData);
                ServiceContainer.snackbar.showSuccess(i18n.getMessage('notifier.subscription.success'), 5000);
            } catch (e) {
                ServiceContainer.snackbar.showError(i18n.getMessage('notifier.subscription.error'));
            } finally {
                this.isLoading = false;
                this.closeNotifierDialog();
            }
        }
    }

    private handleCancelClick(e: Event) {
        e.preventDefault();

        this.closeNotifierDialog();
    }

    private openNotifierDialog() {
        this.isOpen = true;
    }

    private closeNotifierDialog() {
        if (!this.isOpen) return;

        this.isOpen = false;
        this.el.focus();
    }

    private validateForm() {
        this.showValidation = !this.privacyPolicyAccepted || !this.isEmailValid;
        this.hasValidForm = !this.showValidation;
    }


    private renderNotifierDisclaimerText() {
        const disclaimerBulletPoints = i18n.getMessageArray('notifier.disclaimer.bullet-points');

        if (disclaimerBulletPoints.length > 0) {
			return [
                <div>
                    <p>{i18n.getMessage('notifier.disclaimer.label')}</p>
                    <ul>
                        {disclaimerBulletPoints.map((bulletPoint) => (
                            <li innerHTML={bulletPoint}></li>
                        ))}
                    </ul>
                </div>
			];
		} else {
			return null;
		}
    }

    renderContent(): JSX.Element {
        return (
            <Fragment>
                <p>{i18n.getMessage("notifier.form.description")}</p>
                <form class="notifier__form">
                    <nj-input-field
                        class={{
                            'show-validation': this.showValidation,
                            'invalid-email-validation': this.isEmailValid === false
                        }}
                        label={i18n.getMessage("passenger-data.contact-data.email")}
                        name="email"
                        elementId={"email"}
                        type={"email"}
                        required={true}
                        value=""
                        onInput={(e) => this.handleEmailInput(e)}
                        errorMessageId={"emailErrorMessage"}
                        errorMessage={i18n.getMessage("passenger-data.contact-data.email.validation-error")}
                        showValidationMessage={this.showValidation && !this.isEmailValid}
                    />
                    {this.renderNotifierDisclaimerText()}
                    <label
                        class={{
                            'notifier__checkbox': true,
                            'notifier__checkbox--invalid': this.showValidation && !this.privacyPolicyAccepted
                        }}
                    >
                        <input
                            type="checkbox"
                            name="nj-privacy-policy"
                            id="nj-privacy-policy"
                            checked={this.privacyPolicyAccepted}
                            onChange={(e: Event) => this.handlePrivacyPolicyCheckbox(e)}
                            aria-describedby={"privacyPolicyAcceptanceError"}
                        />
                        <span>
                            {i18n.getMessage('notifier.privacy-policy.beforeLinkCheckbox')}{' '}
                            <a class="privacy-link" href={getPrivacyPolicyLink(i18n.getLang())} target="_blank">
                                {i18n.getMessage('notifier.privacy-policy.linkTextCheckbox')}
                            </a>
                            {' '}{i18n.getMessage('notifier.privacy-policy.afterLinkCheckbox')}
                        </span>
                    </label>
                    {this.showValidation && !this.privacyPolicyAccepted && (
                        <span id="privacyPolicyAcceptanceError" class="notifier__error-message">
                            {i18n.getMessage('notifier.privacy-policy.validation-error')}
                        </span>
                    )}
                    <button type="submit"
                        class="button button--primary"
                        onClick={(e) => this.handleSubmitClick(e)}
                    >
                        <bell-icon /> {i18n.getMessage('notifier.submit.button')}
                    </button>
                    <button type="cancel"
                        onClick={(e) => this.handleCancelClick(e)}
                        class="notifier__cancel"
                    >
                        <x-icon aria-hidden="true" width={17.5} height={17.5}/>
                        <span class="screen-reader-text">
                            {i18n.getMessage('notifier.cancel.button')}
                        </span>
                    </button>
                </form>
            </Fragment>
        )
    }

    render() {
        return (
            <div class={{
                'notifier': true,
                'notifier--open': this.isOpen
            }}>
                <button class="notifier__toggler" onClick={(e) => this.handleTogglerClick(e)}>
                    <bell-icon />
                    {i18n.getMessage('notifier.label', 'Notify')}
                </button>
                <div class="notifier__dialog">
                    <h2 class="notifier__title">
                        {i18n.getMessage("notifier.form.title")}
                    </h2>

                    {this.isLoading ?
                        <nj-loader size={NjLoaderSize.SMALL} /> :
                        this.renderContent()
                    }
                </div>
            </div>
        );
    }
}
